'use client';

/**
 * Header de la recherche d'entités
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'affichage du header (titre + SearchBox)
 *
 * Principe Clean Code:
 * - Composant presentational pur
 * - Pas de logique métier
 * - Props explicites et typées
 *
 * @module EntitySearchHeader
 */

import { useMemo } from 'react';
import type { SearchBoxProps } from 'react-instantsearch';
import { SearchBox } from 'react-instantsearch';

// ============================================================================
// Utilities
// ============================================================================

/**
 * Utilitaire de debounce
 * Retarde l'exécution d'une fonction jusqu'à ce qu'un certain temps se soit écoulé
 */
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// ============================================================================
// Types
// ============================================================================

export interface EntitySearchHeaderProps {
  /**
   * Titre de la page
   * @example "Nos auteurs", "Nos éditeurs"
   */
  title?: string;

  /**
   * Placeholder du champ de recherche
   * @example "Rechercher un auteur par nom, prénom..."
   */
  placeholder: string;

  /**
   * Description SEO optionnelle
   */
  description?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Délai de debounce pour la recherche (en ms)
 * Évite de déclencher trop de requêtes pendant que l'utilisateur tape
 */
const SEARCH_DEBOUNCE_MS = 300;

// ============================================================================
// Component
// ============================================================================

/**
 * Header de la page de recherche d'entités
 *
 * Contient:
 * - Titre de la page
 * - Champ de recherche (SearchBox) avec debounce
 * - Description optionnelle
 *
 * @example
 * ```tsx
 * <EntitySearchHeader
 *   title="Nos auteurs"
 *   placeholder="Rechercher un auteur..."
 * />
 * ```
 */
export function EntitySearchHeader({
  title,
  placeholder,
  description,
}: EntitySearchHeaderProps) {
  /**
   * Hook de debounce pour la recherche
   * Mémorisé pour éviter de recréer la fonction debounced à chaque render
   */
  const queryHook: SearchBoxProps['queryHook'] = useMemo(() => {
    const debouncedSearch = debounce(
      (query: string, search: (value: string) => void) => {
        search(query);
      },
      SEARCH_DEBOUNCE_MS,
    );

    return (query: string, search: (value: string) => void) => {
      debouncedSearch(query, search);
    };
  }, []);

  return (
    <div className="mx-auto mt-8 space-y-4">
      {/* Titre */}
      {title && (
        <div className="text-3xl font-bold text-gray-900 lg:text-4xl">
          {title}
        </div>
      )}

      {/* Description optionnelle */}
      {description && <p className="text-lg text-gray-600">{description}</p>}

      {/* SearchBox avec debounce */}
      <div className="mx-auto max-w-2xl">
        <SearchBox
          placeholder={placeholder}
          queryHook={queryHook}
          classNames={{
            root: 'relative',
            form: 'relative',
            input:
              'w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 text-base focus:border-primary-900 focus:outline-none focus:ring-1 focus:ring-primary-900',
            submit:
              'absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-none p-2   focus:outline-none focus:ring-1 focus:ring-primary-900',
            submitIcon: 'text-white',
            reset:
              'absolute right-14 top-1/2 -translate-y-1/2 rounded-md p-2 text-gray-400 hover:text-gray-600 focus:outline-none',
            loadingIndicator: 'absolute right-14 top-1/2 -translate-y-1/2',
          }}
        />
      </div>
    </div>
  );
}
